import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../data/siteData";
import AdminWorkspace from "../components/admin/AdminWorkspace";

const ADMIN_REFRESH_INTERVAL_MS = 30_000;

async function fetchAdminData() {
  const [overviewResponse, requestsResponse, documentsResponse] = await Promise.all([
    fetch(`${API}/admin/overview`, { credentials: "include" }),
    fetch(`${API}/admin/access-requests`, { credentials: "include" }),
    fetch(`${API}/admin/documents`, { credentials: "include" }),
  ]);
  if (!overviewResponse.ok || !requestsResponse.ok || !documentsResponse.ok) throw new Error();
  const activityResponse = await fetch(`${API}/admin/activity`, { credentials: "include" });
  return {
    overview: await overviewResponse.json(),
    requests: await requestsResponse.json(),
    documents: await documentsResponse.json(),
    activity: activityResponse.ok ? await activityResponse.json() : [],
  };
}

export default function AdminPage() {
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [state, setState] = useState("loading");
  const [uploadState, setUploadState] = useState("idle");
  const [resendingEntity, setResendingEntity] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState("");
  const load = useCallback(async () => {
    try {
      const adminData = await fetchAdminData();
      setOverview(adminData.overview);
      setRequests(adminData.requests);
      setDocuments(adminData.documents);
      setActivity(adminData.activity);
      setState("ready");
    } catch {
      setState("denied");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const refreshTimer = window.setInterval(load, ADMIN_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(refreshTimer);
  }, [load]);
  const decide = async (requestId, status) => { await fetch(`${API}/admin/access-requests/${requestId}`, { method:"PATCH", credentials:"include", headers:{"Content-Type":"application/json"}, body:JSON.stringify({status, note:""}) }); load(); };
  const uploadDocument = async (event) => { event.preventDefault(); const formElement = event.currentTarget; setUploadState("loading"); try { const form = new FormData(formElement); const response = await fetch(`${API}/admin/documents`, { method:"POST", credentials:"include", body:form }); if (!response.ok) throw new Error(); formElement.reset(); setUploadState("success"); load(); } catch { setUploadState("error"); } };
  const downloadCsv = async (path, filename) => { const response = await fetch(`${API}${path}`, { credentials: "include" }); if (!response.ok) return; const blob = await response.blob(); const url = URL.createObjectURL(blob); const anchor = window.document.createElement("a"); anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url); };
  const resendEmails = async (entityId) => { setResendingEntity(entityId); try { await fetch(`${API}/admin/activity/${entityId}/resend`, { method: "POST", credentials: "include" }); await load(); } finally { setResendingEntity(""); } };
  const refreshActivity = async () => { setIsRefreshing(true); try { await load(); } finally { setIsRefreshing(false); } };
  const deleteDocument = async (document) => { if (!window.confirm(`Remove ${document.title} from the investor library?`)) return; setDeletingDocument(document.document_id); try { const response = await fetch(`${API}/admin/documents/${document.document_id}`, { method: "DELETE", credentials: "include" }); if (!response.ok) throw new Error(); await load(); } finally { setDeletingDocument(""); } };
  if (state === "loading") return <main className="portal-state" data-testid="admin-loading">Loading administrator workspace…</main>;
  if (state === "denied") return <main className="portal-state" data-testid="admin-denied"><h1>ADMINISTRATOR ACCESS REQUIRED</h1><p>This workspace is restricted to GAMEDAY Holdings administrators.</p><Link className="button button--red" to="/investor-portal" data-testid="return-to-portal-link">Return to portal</Link></main>;
  return <AdminWorkspace overview={overview} requests={requests} activity={activity} documents={documents} uploadState={uploadState} onDecision={decide} onUpload={uploadDocument} onDeleteDocument={deleteDocument} deletingDocument={deletingDocument} onExportActivity={() => downloadCsv("/admin/activity/export.csv", "gameday-activity.csv")} onExportLeads={() => downloadCsv("/admin/leads/export.csv", "gameday-leads.csv")} onResend={resendEmails} onRefresh={refreshActivity} resendingEntity={resendingEntity} isRefreshing={isRefreshing} />;
}